/**
 * "For you" on the Project Dashboard's What's new: the changes in the
 * timeframe that concern the viewer — comments mentioning them, issues
 * assigned to them, and new issues in the disciplines they are responsible
 * for (set on /contacts). Pure, so the rules are tested without a database.
 */

import {
  rowsInWindow,
  type IsDoneStatus,
  type RecentChangeWindow,
  type RecentIssueActivityRow,
} from "./summary";

/** A mention on an issue comment (`pm_issue_comment_mentions`). */
export interface IssueCommentMentionRow {
  comment_id: number;
  created_at: string;
  issue_id: number;
  issue_number: number;
  issue_title: string;
  body: string;
  author_id: number;
}

/**
 * A mention on a document comment that lives on the document side
 * (`document_comment_mentions`) — a thread whose writer unticked "Create
 * issue". Threads filed as issues keep their comments in `pm_issue_comments`,
 * so their mentions arrive as `IssueCommentMentionRow`s instead.
 */
export interface DocumentCommentMentionRow {
  comment_id: number;
  created_at: string;
  thread_id: number;
  document_id: number;
  body: string;
  author_id: number;
}

export interface MentionRows {
  issueComments: IssueCommentMentionRow[];
  documentComments: DocumentCommentMentionRow[];
  /**
   * The document thread an issue IS, keyed by issue id — present when the
   * issue is a document comment thread filed as a sub-issue
   * (`document_comment_threads.linked_issue_id`). A mention on such an issue
   * is a document comment and links to the thread, not the issue.
   */
  threadByIssueId: Map<number, { threadId: number; documentId: number }>;
  filenameByDocumentId: Map<number, string>;
  authorNameById: Map<number, string>;
}

export type ForYouItem =
  | {
      kind: "mention";
      key: string;
      at: string;
      href: string;
      actorName: string;
      /** "#12 Title" for an issue, the filename for a document. */
      subject: string;
      target: "issue" | "document";
      snippet: string;
    }
  | {
      kind: "assigned";
      key: string;
      at: string;
      href: string;
      actorName: string;
      subject: string;
    }
  | {
      kind: "discipline";
      key: string;
      at: string;
      href: string;
      discipline: string;
      subject: string;
    };

export interface ForYouInput {
  buildingId: number;
  projectId: number;
  me: number;
  now: number;
  window: RecentChangeWindow;
  activity: RecentIssueActivityRow[];
  mentions: MentionRows | undefined;
  myDisciplineIds: number[];
  isDone: IsDoneStatus;
}

const SNIPPET_LENGTH = 120;

function snippetOf(body: string): string {
  const flat = body.replace(/\s+/g, " ").trim();
  return flat.length > SNIPPET_LENGTH ? `${flat.slice(0, SNIPPET_LENGTH - 1)}…` : flat;
}

export function issueSubject(issueNumber: number, title: string): string {
  return `#${issueNumber} ${title}`;
}

function metaString(row: RecentIssueActivityRow, key: string): string {
  const value = row.metadata?.[key];
  return typeof value === "string" ? value : "";
}

function jsonNumber(value: unknown, key: string): number | null {
  if (value == null || typeof value !== "object") return null;
  const raw = (value as Record<string, unknown>)[key];
  const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : null;
}

export function issueHref(buildingId: number, projectId: number, issueId: number): string {
  // `project=` too: a cold Issues page without it asks the viewer to choose one.
  return `/buildings/${buildingId}/issues?project=${projectId}&selected=${issueId}`;
}

export function threadHref(buildingId: number, threadId: number): string {
  return `/buildings/${buildingId}/documents?comments=${threadId}`;
}

function issueMention(
  row: IssueCommentMentionRow,
  mentions: MentionRows,
  buildingId: number,
  projectId: number
): ForYouItem {
  const thread = mentions.threadByIssueId.get(row.issue_id);
  const common = {
    kind: "mention" as const,
    key: `mention:pm:${row.comment_id}`,
    at: row.created_at,
    actorName: mentions.authorNameById.get(row.author_id) ?? "",
    snippet: snippetOf(row.body),
  };
  return thread
    ? {
        ...common,
        href: threadHref(buildingId, thread.threadId),
        subject: mentions.filenameByDocumentId.get(thread.documentId) ?? row.issue_title,
        target: "document",
      }
    : {
        ...common,
        href: issueHref(buildingId, projectId, row.issue_id),
        subject: issueSubject(row.issue_number, row.issue_title),
        target: "issue",
      };
}

function documentMention(
  row: DocumentCommentMentionRow,
  mentions: MentionRows,
  buildingId: number
): ForYouItem {
  return {
    kind: "mention",
    key: `mention:doc:${row.comment_id}`,
    at: row.created_at,
    href: threadHref(buildingId, row.thread_id),
    actorName: mentions.authorNameById.get(row.author_id) ?? "",
    subject: mentions.filenameByDocumentId.get(row.document_id) ?? "",
    target: "document",
    snippet: snippetOf(row.body),
  };
}

function mentionItems(input: ForYouInput): ForYouItem[] {
  const { mentions, buildingId, projectId, now, window } = input;
  if (!mentions) return [];
  return [
    ...rowsInWindow(mentions.issueComments, now, window).map((row) =>
      issueMention(row, mentions, buildingId, projectId)
    ),
    ...rowsInWindow(mentions.documentComments, now, window).map((row) =>
      documentMention(row, mentions, buildingId)
    ),
  ];
}

interface AssignedItems {
  items: ForYouItem[];
  issueIds: Set<number>;
}
interface AssignmentCollector extends AssignedItems {
  unassigned: Set<number>;
}

function assignmentUserId(row: RecentIssueActivityRow): number | null {
  return row.action_type === "assignee_removed"
    ? (jsonNumber(row.old_value, "user_id") ?? jsonNumber(row.new_value, "user_id"))
    : jsonNumber(row.new_value, "user_id");
}

function collectAssignment(
  row: RecentIssueActivityRow,
  input: ForYouInput,
  collected: AssignmentCollector
): void {
  const { unassigned, issueIds, items } = collected;
  if (assignmentUserId(row) !== input.me) return;
  if (row.action_type === "assignee_removed") {
    unassigned.add(row.issue_id);
    return;
  }
  if (unassigned.has(row.issue_id) || issueIds.has(row.issue_id)) return;
  if (row.actor_id === input.me || input.isDone(row.issue_status)) return;
  issueIds.add(row.issue_id);
  items.push({
    kind: "assigned",
    key: `assigned:${row.issue_id}`,
    at: row.created_at,
    href: issueHref(input.buildingId, input.projectId, row.issue_id),
    actorName: metaString(row, "actor_name"),
    subject: issueSubject(row.issue_number, row.issue_title),
  });
}

/**
 * Issues assigned to the viewer by someone else in the timeframe, one item per
 * issue (the newest assignment). Assigning yourself is not news, an assignment
 * undone later in the timeframe no longer stands, and an issue finished since
 * is no longer something to act on.
 */
function assignedItems(input: ForYouInput, inWindow: RecentIssueActivityRow[]): AssignedItems {
  // Newest-first, so an unassignment is seen before the assignment it undoes.
  const collected: AssignmentCollector = { items: [], issueIds: new Set(), unassigned: new Set() };
  for (const row of inWindow) {
    if (row.action_type !== "assignee_added" && row.action_type !== "assignee_removed") continue;
    collectAssignment(row, input, collected);
  }
  return { items: collected.items, issueIds: collected.issueIds };
}

interface DisciplineCollector {
  mine: Set<number>;
  removed: Set<string>;
  seen: Set<number>;
  excludeIssueIds: Set<number>;
  items: ForYouItem[];
}

function activityDiscipline(row: RecentIssueActivityRow): number | null {
  return row.action_type === "discipline_added" || row.action_type === "discipline_removed"
    ? jsonNumber(row.new_value, "discipline_id")
    : null;
}

function collectDiscipline(
  row: RecentIssueActivityRow,
  input: ForYouInput,
  collected: DisciplineCollector
): void {
  const { mine, removed, seen, excludeIssueIds, items } = collected;
  const disciplineId = activityDiscipline(row);
  if (disciplineId === null || !mine.has(disciplineId)) return;
  const pair = `${row.issue_id}:${disciplineId}`;
  if (row.action_type === "discipline_removed") {
    removed.add(pair);
    return;
  }
  if (removed.has(pair)) return;
  if (row.actor_id === input.me || seen.has(row.issue_id) || excludeIssueIds.has(row.issue_id))
    return;
  seen.add(row.issue_id);
  items.push({
    kind: "discipline",
    key: `discipline:${row.issue_id}`,
    at: row.created_at,
    href: issueHref(input.buildingId, input.projectId, row.issue_id),
    discipline: metaString(row, "discipline"),
    subject: issueSubject(row.issue_number, row.issue_title),
  });
}

/**
 * Issues newly in one of the viewer's disciplines: created with it, or tagged
 * with it, in the timeframe. Creating an issue writes a `discipline_added` row
 * per discipline, so both cases are that row. A tag removed again later in the
 * timeframe cancels it; the viewer's own tagging is not news.
 */
function disciplineItems(
  input: ForYouInput,
  inWindow: RecentIssueActivityRow[],
  excludeIssueIds: Set<number>
): ForYouItem[] {
  if (input.myDisciplineIds.length === 0) return [];
  // Newest-first, so a removal is seen before the addition it undoes.
  const collected: DisciplineCollector = {
    mine: new Set(input.myDisciplineIds),
    removed: new Set(),
    seen: new Set(),
    excludeIssueIds,
    items: [],
  };
  for (const row of inWindow) collectDiscipline(row, input, collected);
  return collected.items;
}

/** Every "For you" item in the timeframe, newest first. */
export function buildForYouItems(input: ForYouInput): ForYouItem[] {
  const inWindow = rowsInWindow(input.activity, input.now, input.window);
  const assigned = assignedItems(input, inWindow);
  return [
    ...mentionItems(input),
    ...assigned.items,
    ...disciplineItems(input, inWindow, assigned.issueIds),
  ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}
